import type { Rule, RuleAction, RuleCondition, RuleConditionKey } from "../../state/formVersion";
import { RuleActionKey } from "../../state/formVersion";
import type { FieldConfig, VariableConfig } from "../../fields/types";
import type { EmptyCondition, RuleActionDto, RuleConditionDto, RuleDto } from "../../services/ApiService";
import { isEmpty, isNil } from "lodash-es";
import { ACTION_FIELD_VALUE_TYPES } from "../../constants";
import { getPlaceholderDataNames } from "../placeholderUtil";
import FieldId from "../../fields/FieldId";
import type SubformId from "../../state/SubformId";
import RuleId from "../../fields/RuleId";
import VariableId from "../../variables/VariableId";
import type { RequiredBy } from "../../types";
import { Scope } from "../../types";

export const toRuleDto = (rule: Rule, fields: FieldConfig[], variables: VariableConfig[]): RuleDto => ({
  name: rule.name,
  type: rule.operator,
  conditions: rule.conditions
    .filter((condition) => condition.type !== "EMPTY")
    .map((condition): RuleConditionDto => {
      const value = condition.key === "hasValue" ? Boolean(condition.value) : (condition.value ?? "");
      switch (condition.type) {
        case "USER":
          return { type: condition.type, key: condition.key, value };
        case "FIELD":
          return { type: condition.type, key: condition.key, fieldUid: condition.fieldId.toString(), value };
        case "VARIABLE":
          return { type: "FIELD", key: condition.key, fieldUid: condition.variableId.toString(), value };
        case "FIELD_DATA_SOURCE":
          return {
            type: condition.type,
            key: condition.key,
            fieldUid: condition.fieldId?.toString(),
            fieldObjectKey: condition.fieldObjectKey,
            value,
          };
      }
    }),
  actions: rule.actions
    .filter((action) => action.key !== RuleActionKey.EMPTY)
    .map((action) => {
      if (action.key === RuleActionKey.VALUE) {
        return { fieldUid: action.fieldId.toString(), key: "value", value: action.value };
      }
      if (action.key === RuleActionKey.REFERENCE) {
        const ruleValue = action.value;
        let dataName;
        if (ruleValue instanceof FieldId) {
          dataName = fields.find((field) => field.uid.equals(ruleValue))?.properties["data_name"];
        }
        if (ruleValue instanceof VariableId) {
          dataName = variables.find((variable) => variable.uid.equals(ruleValue))?.dataName;
        }
        const value = !isNil(dataName) ? `\${${dataName}}` : undefined;
        return { fieldUid: action.fieldId.toString(), key: "value", value };
      }
      if (action.key === RuleActionKey.VISIBLE || action.key === RuleActionKey.HIDDEN) {
        return { fieldUid: action.fieldId.toString(), key: "visible", value: action.key === RuleActionKey.VISIBLE };
      }
      return { fieldUid: action.fieldId.toString() };
    }),
});

const isNonEmptyAction = (action: RuleActionDto): action is RequiredBy<RuleActionDto, "fieldUid"> =>
  !isNil(action.fieldUid);

const toRuleActions = (
  ruleDto: RuleDto,
  fieldMap: Map<string, FieldConfig>,
  variableMap: Map<string, VariableConfig>,
) => {
  // filter identical actions, as only the last action will be triggered
  const seenKeys = new Set<string>();
  return ruleDto.actions
    .reverse() // process in reverse to naturally keep the last occurrence
    .filter((action) => isNonEmptyAction(action))
    .filter((action) => {
      const combinedKey = `${action.fieldUid}_${action.key}`;
      if (seenKeys.has(combinedKey)) {
        return false;
      }
      seenKeys.add(combinedKey);
      return true;
    })
    .map((action): RuleAction => {
      if (action.key === "value") {
        const field = fieldMap.get(action.fieldUid);
        if (!isNil(field?.type) && !isEmpty(action.value) && ACTION_FIELD_VALUE_TYPES.includes(field?.type)) {
          // Set to field key if placeholder is detected
          const placeholderDataName = getPlaceholderDataNames(action.value?.toString()).at(0);

          if (!isEmpty(placeholderDataName)) {
            const placeholderField = [...fieldMap.values()].find(
              (field) => field.properties["data_name"] === placeholderDataName,
            );
            if (!isNil(placeholderField)) {
              return {
                fieldId: FieldId.from(action.fieldUid),
                key: RuleActionKey.REFERENCE,
                value: placeholderField?.uid,
              };
            }
            const placeholderVariable = [...variableMap.values()].find(
              (variable) => variable.dataName === placeholderDataName,
            );
            return {
              fieldId: FieldId.from(action.fieldUid),
              key: RuleActionKey.REFERENCE,
              value: placeholderVariable?.uid,
            };
          }
        }
        return { value: action.value, key: RuleActionKey.VALUE, fieldId: FieldId.from(action.fieldUid) };
      }
      if (action.key === RuleActionKey.VISIBLE) {
        return {
          key: action.value === true ? RuleActionKey.VISIBLE : RuleActionKey.HIDDEN,
          fieldId: FieldId.from(action.fieldUid),
        };
      }
      return { fieldId: FieldId.from(action.fieldUid) } as RuleAction;
    })
    .reverse(); // Flip it back
};

const isEmptyCondition = (condition: RuleConditionDto): condition is EmptyCondition => isNil(condition.type);

export const toRule = (
  ruleDto: RuleDto,
  fieldMap: Map<string, FieldConfig>,
  variableMap: Map<string, VariableConfig>,
  subformId?: SubformId,
): Rule => ({
  id: RuleId.random(),
  scope: subformId ?? Scope.ROOT,
  name: ruleDto.name,
  operator: ruleDto.type,
  conditions: ruleDto.conditions.map((condition): RuleCondition => {
    if (isEmptyCondition(condition)) {
      return { type: "EMPTY" };
    }
    const value = condition.key === "hasValue" ? Boolean(condition.value) : condition.value;
    switch (condition.type) {
      case "USER":
        return { type: condition.type, key: condition.key as RuleConditionKey, value };
      case "FIELD":
        if (variableMap.has(condition.fieldUid)) {
          return {
            type: "VARIABLE",
            key: condition.key,
            variableId: VariableId.from(condition.fieldUid),
            value,
          };
        }
        return { type: "FIELD", key: condition.key, fieldId: FieldId.from(condition.fieldUid), value };
      case "FIELD_DATA_SOURCE":
        return {
          type: condition.type,
          key: condition.key,
          fieldId: FieldId.from(condition.fieldUid),
          fieldObjectKey: condition.fieldObjectKey,
          value,
        };
    }
  }),
  actions: toRuleActions(ruleDto, fieldMap, variableMap),
});
